import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { Box, Button, Divider, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { UserRole } from "../auth/auth";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import { sanitizeHtml } from "../misc/sanitizeHtml";
import type { RewardResponse } from "../rewards/rewardsApi";
import { ImageCarousel } from "./ImageCarousel";
import { useBuyReward } from "./useBuyReward";
import { useCanBuyReward } from "./useCanBuyReward";
import { usePublicReward } from "./usePublicReward";

interface Props {
  readonly value: RewardResponse;
  readonly canBuy: boolean;
  readonly canBuyMessage?: string;
}

const RewardView: FC<Props> = ({ value, canBuy, canBuyMessage }) => {
  const { buyReward } = useBuyReward();
  const [confirmationDialog, setConfirmationDialog] = useState<{
    content: string;
    onConfirm: () => Promise<void>;
  }>();
  const navigate = useNavigate();
  const handleBuyReward = (value: RewardResponse) =>
    setConfirmationDialog({
      content: `Czy chcesz zakupić nagrodę?`,
      onConfirm: async () => {
        await buyReward(value);
        setConfirmationDialog(undefined);
      },
    });
  const images = value.image ? [value.image] : [];
  return (
    <>
      {/* Pasek nawigacji powrotnej */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/rewards")}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontWeight: "bold",
            "&:hover": { color: "primary.main", bgcolor: "transparent" },
          }}
        >
          Wróć do katalogu nagród
        </Button>
      </Box>
      {/* ZMIENIONE PROPORCJE CSS GRID - Szersza lewa kolumna (8.5fr), węższa prawa (3.5fr) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // Na telefonach: 1 kolumna
            md: "7fr 3fr", // Od 900px wzwyż: Lewa 70%, prawa 30%
            lg: "8.5fr 3.5fr", // Na Full HD: Jeszcze szersza lewa strona (ok. 71% do 29%)
          },
          gap: { xs: 3, md: 4 },
          alignItems: "start", // KLUCZOWE: Utrzymuje "sticky" w prawej kolumnie
        }}
      >
        {/* LEWA STRONA (Zdjęcia, Opis, Program) */}
        <Box>
          <ImageCarousel images={images} />
          {/* NAGŁÓWEK I OPIS WYCIECZKI */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: "bold",
                lineHeight: 1.2,
              }}
            >
              {value.name}
            </Typography>
            <Box
              data-testid="description-render"
              sx={{
                width: "100%",
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(value.description || "")) }}
            />
          </Box>
        </Box>
        <ConfirmationDialog
          open={!!confirmationDialog}
          title="Zakup nagrody"
          content={confirmationDialog?.content}
          onCancel={() => setConfirmationDialog(undefined)}
          onConfirm={confirmationDialog?.onConfirm}
        />
        <Box sx={{ position: "sticky", top: 100 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.1)",
              bgcolor: "#fff",
              boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                fontWeight: "bold",
                mb: 1,
              }}
            >
              Koszt nagrody
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                mb: 3,
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 32, color: "#e6a800" }} />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: "#b38200",
                }}
              >
                {value.cost.toLocaleString()}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  pt: 1,
                }}
              >
                pkt
              </Typography>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<ShoppingCartCheckoutIcon />}
                disabled={!canBuy}
                onClick={() => handleBuyReward(value)}
                sx={{
                  py: 1.8,
                  fontSize: "1.1rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "0 8px 16px rgba(13, 110, 253, 0.2)",
                }}
              >
                {canBuyMessage ? canBuyMessage : "Kup za punkty"}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.disabled",
                mt: 3,
              }}
            >
              *Zakup wycieczki nie podlega zwrotowi punktów.
            </Typography>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

const PublicRewardPage: FC<{ role: UserRole | null }> = ({ role }) => {
  const { reward, isLoading: isPublicRewardLoading } = usePublicReward();
  const { canBuy, isLoading: isCanBuyLoading, message: canBuyMessage } = useCanBuyReward(reward ?? null, role);
  const isLoading = isPublicRewardLoading || isCanBuyLoading;
  return (
    <Box sx={{ animation: "fadeIn 0.4s ease-out" }}>
      {isLoading && <LinearProgress />}
      {reward && <RewardView value={reward} canBuyMessage={canBuyMessage ?? undefined} canBuy={canBuy} />}
    </Box>
  );
};

export default PublicRewardPage;
